import { UserData } from './user-data';

export enum DiscountType {
    Pound = 2,
    Percent = 1,
}

export type SkuPriceTotals = { // in general final prices for the item/basket
    discount: number; // total discount
    net: number; //
    vat: number; // 0
    gross: number; // 0
    qty: number; // 0
    rewardDiscount: number; // 0,
    productDiscount: number; // 0,
    voucherDiscount: number; // 0
    preDiscount: {
        net: number; // 0,
        vat: number; // 0,
        gross: number; // 0,
    };
};

export type SkuPrice = { // pricing per peace
    totals: SkuPriceTotals; // totals are prices that should
    basePrice: number; // base price used to calculate other prices
    net: number; // price after discount is applied
    vat: number;
    gross: number;
    preDiscount: {
        net: number;
        vat: number;
        gross: number;
    };
    discount: DiscountValue;
    discountToUse: AvailableDiscounts;
    priceUnavailable?: boolean;
};

export type AvailableDiscounts = 'none' | 'reward' | 'product' | 'voucher' | 'admin';

export type DiscountValue = {
    preDiscountValue: number;
    reward: {
        percentage: number;
        value: number;
    };
    product: {
        percentage: number;
        value: number;
    };
};

export type BasketAddress = {
    // it might be the same as address but for now on it's own
    id: string;
    userId: string;
    add1: string;
    add2: string;
    add3: string;
    town: string;
    county: string;
    country: string;
    postcode: string;
    isBilling: boolean;
    defaultDeliveryAddress: boolean;
};

export const defaultBasketAddress: BasketAddress = {
    id: '',
    userId: '',
    add1: '',
    add2: '',
    add3: '',
    town: '',
    county: '',
    country: '',
    postcode: '',
    isBilling: false,
    defaultDeliveryAddress: false,
} as BasketAddress;

export type BasketProduct = BespokeBuildProduct & {
    id: string; // '',
    typeId: ProductTypeIds;
    name: string; // '',
    slug: string; // '',
    productCode: string; // ''
    image: string; // ''
    leadtimes: any[];
    masonry: any;
    jobLot: number;
    allowBreakage: number;
    isBespoke?: boolean;
    supplier?: {
        id: string;
    };
    rangeId?: number;
    finishId?: any;
    range?: ProductRange;
};

export type BasketItem = {
    pricing: SkuPrice; // on item price with or without discount
    minQty: number;
    qty: number;
    stockFree: number;
    skuId: string;
    stockCheckResult: any;
    sellUnit:
    | {
        name: string;
        id: string;
    }
    | undefined;
    product: BasketProduct;
    specificDateRequested: any;
    quantityMetres: number;
    sample: boolean;
    piecesRequired: number;
    singlePieceSize: number;
    unitPrice: number;
    unitPriceDiscount: number;
    unitPriceDiscountPerc: number;
    unitPriceGross: number;
    unitVatPrice: number;
    totalPrice: number;
    totalDiscount: number;
    totalPricePreDiscount: number;
    productDetailId: number; // 0,
    pricingMethod: {
        id: any;
    };
    weight: number;
    leadtime: number;
    separateShipment: boolean;
    potentialQty: number;
};

export type BasketValues = {
    discount: number;
    gross: number;
    net: number;
    vat: number;
    delivery: number;
    deliveryVat: number;
    // netPreDiscount: number;
    productDiscount: number;
    rewardDiscount: number;
    voucherDiscount: number;
    adminDiscount: number;
    voucher?: number;
    preDiscount: {
        net: number;
        vat: number;
        gross: number;
    };
};

export const defaultSkuPriceTotals: () => SkuPriceTotals = () => {
    return {
        // netPreDiscount: 0,
        discount: 0,
        net: 0,
        vat: 0,
        gross: 0,
        qty: 0,
        rewardDiscount: 0,
        productDiscount: 0,
        voucherDiscount: 0,
        preDiscount: {
            net: 0,
            vat: 0,
            gross: 0,
        }
    } as SkuPriceTotals;
}

export const defaultDiscountValue: () => DiscountValue = () => {
    return {
        preDiscountValue: 0,
        reward: {
            percentage: 0,
            value: 0,
        },
        product: {
            percentage: 0,
            value: 0,
        }
    } as DiscountValue;
};

export const defaultSkuPrice: () => SkuPrice = () => {
    return {
        totals: defaultSkuPriceTotals(),
        basePrice: 0,
        net: 0, // price after discount is applied
        vat: 0,
        gross: 0,
        preDiscount: {
            net: 0,
            vat: 0,
            gross: 0,
        },
        discount: defaultDiscountValue(),
        discountToUse: 'none',
    }
};

export const defaultBasketItem: () => BasketItem = () => {
    return {
        product: { id: '', productCode: '', name: '', slug: '', image: '' },
        sellUnit: undefined,
        quantityMetres: 0,
        skuId: '',
        sample: false,
        qty: 0,
        stockCheckResult: undefined,
        stockFree: 0,
        piecesRequired: 0,
        singlePieceSize: 0,
        totalPrice: 0,
        unitPrice: 0,
        unitPriceDiscount: 0,
        unitPriceGross: 0,
        totalDiscount: 0,
        totalPricePreDiscount: 0,
        pricing: defaultSkuPrice(),
        productDetailId: 0,
        weight: 0,
        leadtime: 0,
        minQty: 0,
    } as BasketItem;
};

export type Basket = {
    source: 'public' | 'admin' | 'unknown'; // 'public'
    type: 'order' | 'quote'; // 'public'
    fulfilmentMethod?: 'delivery' | 'collection';
    noDeliveryRequired?: boolean;
    lastStep: 'entry' | 'checkout'; // 'public'
    customerType: 'trade' | 'customer' | 'new' | 'existing'; // 'public'
    items: Array<BasketItem>; // []
    itemsOriginal?: Array<BasketItem>;
    samples: Array<BasketItem>; // []
    potentialQty: number; // 0
    rewardsDiscountPercentage: number; // 0
    voucher: {
        code: string;
        discountType: DiscountType;
        discountValue: number;
        name: string;
        isExpired: string;

        allowOtherDiscounts?: number,
        createdAt?: string
        createdBy?: number
        deleted?: number,
        endDate?: string
        expiryDate?: string
        id?: number
        startDate?: string,
        status?: number
    } | undefined;
    voucherCodeEntered: string; // ''
    deliveryInstructions: string; // '';
    deliveryPostcode: string; // '';
    qualifiedForFreeDelivery?: any,
    deliveryOptions: {
        premium: any,
        economy: any,
        allowPremium: any,
        geo: {
            travelKM: number,
            travelMiles: number,
            travelMinutes: number,
            lat: number,
            lng: number,
        }
    },
    deliveryRestrictions: Array<string>,
    customDelivery: {
        value: any,
        active: boolean
    },
    differentDeliveryAddress: boolean; // false;
    selectedService: any,
    token: string | undefined; // undefined;
    customer?: UserData & {
        exists: boolean;
        [key: string]: any;
    }; // = undefined;
    deliveryAddress: BasketAddress | undefined; // = undefined;
    deliveryAddresses: BasketAddress[];
    billingAddress: BasketAddress | undefined; // undefined;
    createdBy: UserData | undefined; // undefined;
    sampleOnly: boolean; // false;
    guest: boolean; // true;
    intent?: any;
    paymentMethod: 'CC' | 'BACS' | 'XE' | 'PP';
    shipments: any[];
    discount: { type: 'pound' | 'perc'; value: number }; // { type: 'pound', value: 0,  };
    restrictions: {
        slope: boolean;
        gravel: boolean;
        boards: boolean;
        offload: boolean;
    }; // { slope: false, gravel: false, boards: false, offload: false, }
    values: BasketValues; // = { discount: 0, gross: 0, net: 0, vat: 0, voucherDiscount: 0, delivery: 0, deliveryVat: 0, };
    payment?: {
        paymentMethod: any;
    };
    xeroQuoteId: string;
    hasUnavailableItem?: boolean;
    hasTotalsError?: boolean;
    lastTotalsErrorMessage?: string;
};

export const defaultBasketValues: BasketValues = {
    discount: 0,
    gross: 0,
    net: 0,
    vat: 0,
    productDiscount: 0,
    rewardDiscount: 0,
    voucherDiscount: 0,
    adminDiscount: 0,
    delivery: 0,
    deliveryVat: 0,
    preDiscount: {
        net: 0,
        vat: 0,
        gross: 0
    }
    // netPreDiscount: 0
} as BasketValues;

const toFiniteNumber = (value: any): number | undefined => {
    const parsed = +value;
    return Number.isFinite(parsed) ? parsed : undefined;
};

const roundMoney = (value: number): number => +value.toFixed(2);

const moneyEquals = (left: any, right: any): boolean => {
    const leftNumber = toFiniteNumber(left);
    const rightNumber = toFiniteNumber(right);
    return leftNumber !== undefined && rightNumber !== undefined && Math.abs(leftNumber - rightNumber) < 0.005;
};

const getDiscountAmount = (discount: { value?: number; number?: number } | undefined): number => {
    if (!discount) {
        return 0;
    }

    return +(discount.value ?? discount.number ?? 0);
};

const capMoneyDiscount = (discount: number, maxDiscount: number): number => {
    const finiteDiscount = toFiniteNumber(discount) ?? 0;
    const finiteMax = toFiniteNumber(maxDiscount) ?? 0;
    return Math.min(Math.max(0, roundMoney(finiteDiscount)), Math.max(0, roundMoney(finiteMax)));
};

const isCanonicalLineTotalPricing = (pricing: SkuPrice, qty: number): boolean => {
    if (!pricing || !pricing.totals) {
        return false;
    }

    const totalsNet = toFiniteNumber(pricing.totals.net);
    const totalsPreDiscountNet = toFiniteNumber(pricing.totals.preDiscount && pricing.totals.preDiscount.net);
    const totalsQty = toFiniteNumber(pricing.totals.qty);
    const pricingNet = toFiniteNumber(pricing.net);
    const pricingVat = toFiniteNumber(pricing.vat);
    const preDiscountNet = toFiniteNumber(pricing.preDiscount && pricing.preDiscount.net);
    const totalsVat = toFiniteNumber(pricing.totals.vat);
    const totalsGross = toFiniteNumber(pricing.totals.gross);

    if (totalsQty !== undefined && totalsQty > 0 && !moneyEquals(totalsQty, qty)) {
        return false;
    }

    if (totalsNet === undefined && totalsPreDiscountNet === undefined) {
        return false;
    }

    if ((totalsNet === undefined || totalsNet <= 0) && (totalsPreDiscountNet === undefined || totalsPreDiscountNet <= 0)) {
        return false;
    }

    const unitPriceMultipliesToTotals =
        pricingNet !== undefined &&
        pricingNet > 0 &&
        totalsNet !== undefined &&
        totalsNet > 0 &&
        moneyEquals(pricingNet * qty, totalsNet) &&
        (totalsVat === undefined ||
            pricingVat === undefined ||
            moneyEquals(pricingVat * qty, totalsVat) ||
            moneyEquals(roundMoney((totalsNet / 100) * 20), totalsVat)) &&
        (totalsGross === undefined ||
            moneyEquals(roundMoney(totalsNet + (totalsVat ?? roundMoney((totalsNet / 100) * 20))), totalsGross));

    return (
        unitPriceMultipliesToTotals ||
        moneyEquals(pricingNet, totalsNet) ||
        moneyEquals(preDiscountNet, totalsPreDiscountNet) ||
        (preDiscountNet !== undefined &&
            totalsPreDiscountNet !== undefined &&
            !moneyEquals(preDiscountNet * qty, totalsPreDiscountNet))
    );
};

const getLineDiscountAmount = (pricing: SkuPrice, type: 'product' | 'reward', qty: number, maxDiscount: number): number => {
    const totalsDiscount = toFiniteNumber(pricing.totals && pricing.totals[`${type}Discount`]);
    if (totalsDiscount !== undefined && totalsDiscount > 0) {
        return capMoneyDiscount(totalsDiscount, maxDiscount);
    }

    return capMoneyDiscount(roundMoney(getDiscountAmount(pricing.discount && pricing.discount[type]) * qty), maxDiscount);
};

const calculateCanonicalLineTotalPrice = (pricing: SkuPrice, qty: number): SkuPrice => {
    const totalsNet = toFiniteNumber(pricing.totals && pricing.totals.net);
    const totalsPreDiscountFromTotals = toFiniteNumber(
        pricing.totals && pricing.totals.preDiscount && pricing.totals.preDiscount.net
    );
    const pricingNet = toFiniteNumber(pricing.net);
    const pricingPreDiscountNet = toFiniteNumber(pricing.preDiscount && pricing.preDiscount.net);
    const unitPriceMultipliesToTotals =
        qty > 0 &&
        pricingNet !== undefined &&
        totalsNet !== undefined &&
        moneyEquals(pricingNet * qty, totalsNet);
    const totalsPreDiscountNet =
        (totalsPreDiscountFromTotals !== undefined && totalsPreDiscountFromTotals > 0
            ? totalsPreDiscountFromTotals
            : undefined) ??
        (unitPriceMultipliesToTotals
            ? roundMoney((pricingPreDiscountNet ?? pricingNet) * qty)
            : undefined) ??
        pricingPreDiscountNet ??
        totalsNet ??
        0;

    let productDiscount = 0;
    let rewardDiscount = 0;

    if (pricing.discountToUse === 'product') {
        productDiscount = getLineDiscountAmount(pricing, 'product', qty, totalsPreDiscountNet);
    }

    if (pricing.discountToUse === 'reward') {
        rewardDiscount = getLineDiscountAmount(pricing, 'reward', qty, totalsPreDiscountNet);
    }

    const discount = productDiscount + rewardDiscount;
    const lineNet = roundMoney(totalsPreDiscountNet - discount);
    const lineVat = roundMoney((lineNet / 100) * 20);
    const lineGross = roundMoney(lineNet + lineVat);
    const unitNet = qty > 0 ? roundMoney(lineNet / qty) : lineNet;
    const unitPreDiscountNet = qty > 0 ? roundMoney(totalsPreDiscountNet / qty) : totalsPreDiscountNet;

    pricing.net = unitNet;
    pricing.vat = roundMoney((unitNet / 100) * 20);
    pricing.gross = roundMoney(unitNet + pricing.vat);
    pricing.preDiscount = {
        net: unitPreDiscountNet,
        vat: roundMoney((unitPreDiscountNet / 100) * 20),
        gross: roundMoney(unitPreDiscountNet + roundMoney((unitPreDiscountNet / 100) * 20)),
    };
    pricing.totals = {
        ...pricing.totals,
        qty,
        net: lineNet,
        vat: lineVat,
        gross: lineGross,
        productDiscount,
        rewardDiscount,
        discount,
        preDiscount: {
            net: totalsPreDiscountNet,
            vat: roundMoney((totalsPreDiscountNet / 100) * 20),
            gross: roundMoney(totalsPreDiscountNet + roundMoney((totalsPreDiscountNet / 100) * 20)),
        },
    };

    return pricing;
};

export const CalculateDiscountPrice = (pricing: SkuPrice, qty, onlyApplyDiscount = true): SkuPrice => {
    qty = +qty;

    if (isCanonicalLineTotalPricing(pricing, qty)) {
        return calculateCanonicalLineTotalPrice(pricing, qty);
    }

    pricing.net = +pricing.preDiscount.net;

    if (onlyApplyDiscount && pricing.discountToUse === 'product' ||
        !onlyApplyDiscount && pricing.discount.product && pricing.discount.product.percentage > 0
    ) {
        pricing.discountToUse = 'product';
        pricing.net = +pricing.preDiscount.net - capMoneyDiscount(
            getDiscountAmount(pricing.discount.product),
            +pricing.preDiscount.net,
        );
    }
    if (onlyApplyDiscount && pricing.discountToUse === 'reward' ||
        !onlyApplyDiscount && pricing.discount.reward && +pricing.discount.reward.percentage > +pricing.discount.product.percentage
    ) {
        pricing.discountToUse = 'reward';
        pricing.net = +pricing.preDiscount.net - capMoneyDiscount(
            getDiscountAmount(pricing.discount.reward),
            +pricing.preDiscount.net,
        );
    }

    pricing.vat = +((+pricing.net / 100) * 20);
    pricing.gross = +pricing.net + +pricing.vat;

    pricing.totals = CalculatePricingTotals(pricing, qty);

    return pricing;
};

export const CalculatePricingTotals = (pricing, qty) => {
    qty = +qty;

    if (isCanonicalLineTotalPricing(pricing, qty)) {
        return calculateCanonicalLineTotalPrice(pricing, qty).totals;
    }

    pricing.totals.qty = +qty;
    const unitPreDiscountNet = toFiniteNumber(pricing.preDiscount && pricing.preDiscount.net) ?? toFiniteNumber(pricing.basePrice) ?? 0;
    const linePreDiscountNet = roundMoney(unitPreDiscountNet * qty);
    const linePreDiscountVat = roundMoney((linePreDiscountNet / 100) * 20);
    pricing.totals.preDiscount = {
        net: linePreDiscountNet,
        vat: linePreDiscountVat,
        gross: roundMoney(linePreDiscountNet + linePreDiscountVat),
    };

    pricing.totals.productDiscount = 0;
    pricing.totals.rewardDiscount = 0;

    if (pricing.discountToUse !== 'none') {
        if (pricing.discountToUse === 'product') {
            pricing.totals.productDiscount = capMoneyDiscount(
                getDiscountAmount(pricing.discount.product) * pricing.totals.qty,
                linePreDiscountNet,
            );
        }
        if (pricing.discountToUse === 'reward') {
            pricing.totals.rewardDiscount = capMoneyDiscount(
                getDiscountAmount(pricing.discount.reward) * pricing.totals.qty,
                linePreDiscountNet,
            );
        }
        pricing.totals.discount = pricing.totals.productDiscount + pricing.totals.rewardDiscount;
    }

    pricing.totals.net = pricing.net * qty;
    pricing.totals.vat = +((+pricing.totals.net / 100) * 20);
    pricing.totals.gross = +pricing.totals.net + +pricing.totals.vat;

    return pricing.totals;
};


// Bespoke
export type ProductSellUnits = {
    id: string;
    name: string;
    productType?: string;
    sellUnit?: string;
    sortOrder?: 1 | 0;
};

export type Restrictions = {
    width: {
        min: number;
        max: number;
    };
    length: {
        min: number;
        max: number;
    };
    height: {
        min: number;
        max: number;
    };
};

export type ProductTypeIds = 'paving' | 'piercaps' | 'coping' | 'steps' | 'setts';

export type ProductTypes = {
    adminImageUrl: string;
    allowAdditionalFinish: 1 | 0;
    allowBackEdge: 1 | 0;
    allowBespoke: 1 | 0;
    allowColouring: 1 | 0;
    allowCorners: 1 | 0;
    allowEdgeFinishing: 1 | 0;
    allowEnds: 1 | 0;
    allowFinishBottom: 1 | 0;
    allowFinishTop: 1 | 0;
    allowFreeDelivery: 1 | 0;
    allowPieces: 1 | 0;
    allowRandomLength: 1 | 0;
    allowRandomWidth: 1 | 0;
    allowRiser: 1 | 0;
    allowSize: 1 | 0;
    allowTexture: 1 | 0;
    allowTonneFinish: 1 | 0;
    allowWeathering: 1 | 0;
    bannerUrl: string;
    bespokeShowQuantity: 1 | 0;
    defaultIsRandom: number;
    deleted: 1 | 0;
    description: string;
    id: ProductTypeIds;
    imageUrl: string;
    lengthLabel: string;
    masonryLengthsForCalc: number;
    masonryWidthsForAdminCalc: number;
    masonryWidthsForCalc: number;
    name: string;
    productWeatheringLabel: string;
    randomLength: number;
    randomWidth: number;
    selectQuantityLabel: string;
    sellUnit: string;
    sellUnits: Array<ProductSellUnits>;
    seoDescription: string;
    seoKeywords: string;
    seoTitle: string;
    showInCM: number;
    showInMainMenu: number;
    sidesToCalcForFinishes: number;
    slug: string;
    sortOrder: number;
    status: number;
    widthLabel: string;
};

export type ProductFinish = {
    id: number;
    name: string;
    altName: null | string;
    skuId: string;
    productVariableId: number;
    costingMatrixBased: number;
    available: 1 | 0;
    deleted: 1 | 0;
    createdAt: string;
    size: number;
    cost: number;
    costType: string;
    imageUrl: string;
    sortOrder: number;
    costs: [
        {
            id: number;
            skuId: string;
            ProductVariableSlug: string;
            cost: number;
            rangeMin: 0;
            rangeMax: 9999;
            company: string;
        }
    ];
};

export type FinishesEdges = {
    id: number;
    name: string;
    altName: string;
    skuId: string;
    productVariableId: number;
    costingMatrixBased: number;
    available: 1 | 0;
    deleted: 1 | 0;
    createdAt: string;
    size: number;
    cost: string;
    costType: string;
    imageUrl: string;
    sortOrder: 1 | 0;
    costs: any;
};

export type ProductConfigSubType = {
    id: number;
    name: string;
    altName: string;
    skuId: string;
    productVariableId: number;
    costingMatrixBased: number;
    available: 1 | 0;
    deleted: 0 | 1;
    createdAt: string;
    size: number;
    cost: string;
    costType: string;
    imageUrl: string;
    sortOrder: number;
    costs: any;
    finishesEdge: Array<FinishesEdges>;
    finishesTop: [];
    finishesBottom: [];
    finishTop?: {
        front: string;
        back: string;
        left: string;
        right: string;
    };
    finishEdge?: {
        front: string;
        back: string;
        left: string;
        right: string;
    };
    finishBottom?: {
        front: string;
        back: string;
        left: string;
        right: string;
    };
    finishesAdditional: Array<{ name: string; altName: string; id: string }>;
};

export type ProductConfiguration = {
    id: number;
    name: string;
    altName: string;
    skuId: string;
    productVariableId: number;
    costingMatrixBased: number;
    available: number;
    deleted: number;
    createdAt: string;
    size: number;
    cost: string;
    costType: string;
    imageUrl: string;
    sortOrder: 1 | 0;
    costs: any;
    subTypes: Array<ProductConfigSubType>;
    allowRandom: 1 | 0;
    supplied: boolean;
    leadtimes: Array<any>;
    restrictions: Restrictions;
    sellUnit: ProductSellUnits;
    sellUnits: Array<ProductSellUnits>;
};

export type CostSQM = {
    id: number;
    rangeId: number;
    textureType: number;
    type: string;
    sizingType: string;
    minThickness: number;
    maxThickness: number;
    cost: number;
};

export type ProductRange = {
    id: number;
    supplier: string;
    collectionAddress: string;
    slug: string;
    name: string;
    bespokeName: string;
    deleted: 0 | 1;
    sortOrder: 0 | 1;
    available: 1 | 0;
    createdAt: string; // "2020-03-04T16:17:21.726Z",
    level: number;
    parent: number;
    showInMainMenu: 1 | 0;
    showInPreviewSlider: 0 | 1;
    tileText: string;
    bannerText: string;
    baseCost: string;
    baseWeight: number;
    bespokeImageUrl: string;
    imageUrl: string;
    bannerUrl: string;
    seoTitle: string;
    seoKeywords: string;
    seoDescription: string;
    lastUpdatedAt: string;
    description: string;
    showInCM: 1 | 0;
    margin: number;
    baseCostWithMargin: number;
    costsSQM: {
        random: Array<CostSQM>;
        setWidthRandom: Array<CostSQM>;
        setWidthSetLength: Array<CostSQM>;
    };
};

export type HeightValidation = {
    setBy: string;
    valid: boolean;
    min: number;
    max: number;
};

export type BespokeProductBase = {
    type: undefined | ProductTypes;
    finish: undefined | ProductFinish;
    leadtimes: Array<any>;
    sellUnit: undefined | ProductSellUnits;
    quantity: number;
    length: undefined | number;
    width: undefined | number;
    height: undefined | number;
    widthRandom: boolean;
    lengthRandom: boolean;
    cornersLR: number;
    cornersRR: number;
    ends: number;
    totalCost?: number,
    riserRequired: boolean;
    riser:
    | undefined
    | RiserType;
    unitCostBreakdown: {
        singlePieceSize: number;
        piecesRequired: number;
        squareMetres: number;
        linearMetres: number;

        baseUnitCost?: number;
        unitCost?: number;
        riserUnitCost?: number;
        weathering?: number;
        finishTop?: number;
        finishEdge?: number;
        finishBottom?: number;
        corner?: number;
        cornersLR?: number;
        cornersRR?: number;
        end?: number;
        totalCost?: number,
        totalUnitCost?: number;
        totalCharge?: number;
        sellConversion?: string;
        allowPieces?: number;
    };
    weathering: undefined | WeatheringType;
    purchaseUnit: string;
    pricePerSellUnit?: number;
    finishTop: FinishesEdges | '';
    finishEdge:
    | any
    | {
        front: FinishesEdges | '';
        back: FinishesEdges | '';
        left: FinishesEdges | '';
        right: FinishesEdges | '';
    }
    | FinishesEdges;
    finishBottom:
    | any
    | {
        front: FinishesEdges | '';
        back: FinishesEdges | '';
        left: FinishesEdges | '';
        right: FinishesEdges | '';
    }
    | FinishesEdges;
};

export type BespokeBuildProduct = BespokeProductBase & {
    subTypes: Array<any>;
    supplierId: string;

    sellUnits: undefined | Array<ProductSellUnits>;
    quantityMetres: number;
    weatheringLeft: number;

    copingLengthMeasure?: string;
    totalLength: number;
    weatheringRight: number;

    estDeliveryDate: Date;
    restrictions?: Restrictions;
};

export type RiserType = {
    quantity: number;
    height: number;
    thickness: number;
    weightPerSellUnit: number;
    pricePerSellUnit: number;
    weight: number;
    price: number;
};

export type WeatheringType = {
    id: string;
    skuId: string; // '4W' | 'TW' | 'OW',
    name: string;
    altName: string;
    productVariableId: any;
    size: any;
    cost: any;
    costType: any;
    imageUrl: any;
};

export const defaultBespokeBuildProductBase: BespokeProductBase = {
    type: undefined,
    finish: undefined,
    leadtimes: [],
    sellUnit: undefined,
    quantity: 0,
    length: undefined,
    width: undefined,
    height: 40,
    cornersLR: 0,
    cornersRR: 0,
    ends: 0,
    widthRandom: false,
    lengthRandom: false,
    riserRequired: false,
    riser: undefined,
    purchaseUnit: '',
    unitCostBreakdown: {
        singlePieceSize: 0,
        piecesRequired: 0,
        squareMetres: 0,
        linearMetres: 0,
    },
    finishTop: '',
    finishEdge: {
        front: undefined,
        back: undefined,
        left: undefined,
        right: undefined
    },
    finishBottom: {
        front: '',
        back: '',
        left: '',
        right: '',
        name: '',
    },
    weathering: undefined,
};

export const defaultBespokeBuildProduct: BespokeBuildProduct = {
    ...JSON.parse(JSON.stringify(defaultBespokeBuildProductBase)),
    subTypes: [],
    supplierId: '',
    purchaseUnit: '',
    sellUnits: undefined,
    quantityMetres: 0,
    weatheringLeft: 0,
    totalLength: 0,
    weatheringRight: 0,
    estDeliveryDate: new Date(),
    unitCostBreakdown: {
        squareMetres: 0,
        linearMetres: 0,
        baseUnitCost: 0,
        unitCost: 0,
        singlePieceSize: 0,
        piecesRequired: 0,
        riserUnitCost: 0,
        weathering: 0,
        finishTop: 0,
        finishEdge: 0,
        finishBottom: 0,
        corner: 0,
        cornersLR: 0,
        cornersRR: 0,
        end: 0,
        totalUnitCost: 0,
        totalCharge: 0,
        sellConversion: '',
    }
};

export type BespokeBuild = {
    currentStep: 1 | 2 | 3 | 4 | 5;
    product: BespokeBuildProduct;
};

export const defaultBespokeBuild: BespokeBuild = {
    currentStep: 1,
    product: JSON.parse(JSON.stringify(defaultBespokeBuildProduct)),
} as BespokeBuild;


export type BasketBespoke = BespokeProductBase & {
    productCode: string,
    supplierId: string,
    rangeId: '' | number,
    dimensions?: [number, number, number],
    finishes: { top: undefined, bottom: undefined, edge: undefined, weathering: undefined },
    marginBespoke?: any,
    quantityMetres?: number,
    additionalRequirements?: {
        costType: string | 'PLM'
        cost: number
    }
    leadtime?: number,
    unitCost?: number,
    weightPerSellUnit?: number,
    weightTotal?: string,
    masonry?: any,
    range?: ProductRange
}

export type DimensionSize = {
    trueSize: number,
    size: number,
    skuId?: string
};

export type PricingPayload = {
    isBespoke: true,
    applyDiscount: boolean,
    dimensions: {
        l: DimensionSize,
        w: DimensionSize,
        t: DimensionSize,
    },
    supplierId: string,
    rangeId: number | '',
    productType: ProductTypeIds,
    finishId: number,
    pricingMethod?: any, // product.pricingMethod.id,
    sellUnit: string,
    margin: number,
    discount: number,
    quantity: number,
    finishes: {
        top?: undefined | { skuId: string },
        bottom?: undefined | { skuId: string },
        //edge?: undefined | { skuId: string },
        weathering?: undefined | { skuId: string }
        masonry?: undefined | { skuId: string }
    },
    widthRandom: boolean,
    lengthRandom: boolean,
    extras: PricingPayloadExtras,
    customerId?: string
};

export type PricingPayloadExtras = {
    riser?: {
        height: number,
        quantity: number,
        thickness: number
    },
    cornersRR?: { quantity: number },
    cornersLR?: { quantity: number },
    ends?: { quantity: number }
};

export type PricingResult = {
    errors: Array<any>,
    charging: {
        costingMethod: string, // "CM",
        sellUnit: string // "P"
    },
    sizing: {
        PSM: number,
        LNM: number,
        CM: number,
        edgeFull: number,
        edgeToMason: number
    },
    weight: {
        supplierBaseWeight: number,
        total: number
    },
    quantity: {
        requested: {
            quantity: number,
            sizingType: string, // "P"
        },
        received: {
            P: number,
            PSM: number,
            LNM: number,
            CM: number
        }
    },
    baseStoneCost: {
        PSM: number,
        CM: number,
        P: number
    },
    costOfSale: {
        PSM: number,
        LNM: number,
        CM: number,
        P: number
    },
    costPerPiece: {
        baseStoneCost: number,
        totalCost: {
            piece: number,
            pieces: number
        }
    },
    sellValues: {
        P: number,
        CM: number,
        PLM: number,
        PSM: number
    },
    sellValue: number,
    leadtime: number,
    estDeliveryDate: string, // "2023-08-24T09:53:31.863Z",
    detail: Array<any>,
    rangeData: {
        id: number,
        name: string,
        bespokeName: string,
        bannerText: string,
        description: string,
        imageUrl: string,
        allowRandom: 1 | 0,
        margin: number
    },
    pricing: SkuPrice,
    bespokeId: string,
    pricingLog: Array<string>
}

export const defaultBasketBespoke: BasketBespoke = {
    productCode: '',
    supplierId: '',
    rangeId: '',
    finishes: { top: undefined, bottom: undefined, edge: undefined, weathering: undefined },
    ...JSON.parse(JSON.stringify(defaultBespokeBuildProductBase)),
};
